import { Link } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
    AlertTriangle, Info, Check, ChevronDown, ArrowRightLeft,
    TrendingUp, TrendingDown, Sparkles, Zap, Search, Layers,
} from "lucide-react";
import api, { formatApiError } from "../../api/client";
import { DURATION_OPTIONS, entryFee, entryFeePctLabel, formatNumber } from "../../lib/format";
import BasketBuilder from "./BasketBuilder";

const SHORT_DURATIONS = new Set(["15m", "30m"]);
const LONG_DURATIONS = new Set(["15d", "1mo", "1yr"]);

const NOTIONAL_PRESETS = [100, 500, 1_000, 5_000, 10_000, 50_000];

export default function SwapTicket({ coin, midPrice, onPairChange, onCreated }) {
    const [swapType, setSwapType] = useState("single"); // "single" | "multi" | "basket"
    const [pair, setPair] = useState({ base: coin, quote: "USD" });
    const [choice, setChoice] = useState("BULL"); // BULL | BEAR
    const [orderType, setOrderType] = useState("MARKET"); // MARKET | LIMIT
    const [limitPrice, setLimitPrice] = useState("");
    const [notional, setNotional] = useState("1000");
    const [margin, setMargin] = useState("20");
    const [duration, setDuration] = useState("15m");
    const [busy, setBusy] = useState(false);
    const [pairsData, setPairsData] = useState({ single: [], multi: [] });
    const [crossRef, setCrossRef] = useState(null); // live base/quote ratio for multi limits
    const [basketSelection, setBasketSelection] = useState(null); // payload from BasketBuilder

    // Fetch the live Hyperliquid universe (~230 single pairs + curated cross
    // combos). Refreshes every 60s so newly listed perps appear automatically.
    useEffect(() => {
        let alive = true;
        const load = () => api.get("/oracle/pairs")
            .then((r) => { if (alive) setPairsData(r.data || { single: [], multi: [] }); })
            .catch(() => {});
        load();
        const id = setInterval(load, 60_000);
        return () => { alive = false; clearInterval(id); };
    }, []);

    // Sync pair.base with parent-controlled coin when the external market
    // selector changes (only for single swaps).
    useEffect(() => {
        if (swapType === "single") setPair((p) => ({ ...p, base: coin, quote: "USD" }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coin]);

    // When user picks a pair inside the ticket, bubble base up so the chart
    // and order book follow along.
    const setPairAndBubble = (next) => {
        setPair(next);
        onPairChange?.({ base: next.base, quote: next.quote });
    };

    // Reset pair when switching type
    const setTypeAndPair = (t) => {
        setSwapType(t);
        // LIMIT orders don't make sense for baskets (index isn't a single quote).
        if (t === "basket" && orderType === "LIMIT") setOrderType("MARKET");
        if (t === "single") {
            setPairAndBubble({ base: "BTC", quote: "USD" });
        } else if (t === "multi") {
            setPairAndBubble({ base: "BTC", quote: "ETH" });
        }
        // For basket we keep the parent pair alone — the chart just keeps
        // showing whatever coin was selected previously; the basket ticket
        // is fully self-contained below.
    };

    const minPct = SHORT_DURATIONS.has(duration) ? 0.01 : 0.02;
    const maxLev = SHORT_DURATIONS.has(duration) ? 100 : 50;
    const minMargin = Math.max(1, (parseFloat(notional) || 0) * minPct);
    const leverage = (parseFloat(notional) || 0) / (parseFloat(margin) || 1);
    const fee = entryFee(notional, duration);

    const warning = useMemo(() => {
        const m = parseFloat(margin) || 0;
        const n = parseFloat(notional) || 0;
        if (n <= 0 || m <= 0) return null;
        if (m < minMargin) return { level: "error", msg: `Below ${(minPct * 100).toFixed(0)}% minimum for ${duration}. Required ≥ ${minMargin.toFixed(2)} USDC.` };
        const pct = (m / n) * 100;
        if (pct < minPct * 100 + 0.5) return { level: "warning", msg: `Margin at the floor (${pct.toFixed(2)}%). Tiny adverse moves can wipe it — consider adding buffer.` };
        if (pct < minPct * 100 + 2) return { level: "info", msg: `High leverage (${leverage.toFixed(1)}×). A ${(100 / leverage).toFixed(2)}% adverse move closes the position.` };
        return null;
    }, [margin, notional, minMargin, minPct, duration, leverage]);

    // For multi-swap limits we need the live base/quote ratio so the user
    // sees an accurate "current ratio" reference next to the limit input.
    useEffect(() => {
        if (swapType !== "multi" || orderType !== "LIMIT") { setCrossRef(null); return; }
        let alive = true;
        const fetchRef = async () => {
            try {
                const [a, b] = await Promise.all([
                    api.get(`/oracle/price/${pair.base}`),
                    api.get(`/oracle/price/${pair.quote}`),
                ]);
                if (!alive) return;
                const bp = a?.data?.price, qp = b?.data?.price;
                if (bp && qp) setCrossRef(bp / qp);
            } catch { /* live mid still useful */ }
        };
        fetchRef();
        const id = setInterval(fetchRef, 4000);
        return () => { alive = false; clearInterval(id); };
    }, [swapType, orderType, pair.base, pair.quote]);

    const submit = async () => {
        setBusy(true);
        try {
            const payload = {
                side: choice,
                notional: parseFloat(notional),
                margin: parseFloat(margin),
                duration,
                order_type: orderType,
            };
            if (swapType === "basket") {
                if (!basketSelection || (!basketSelection.basket_id && !basketSelection.basket)) {
                    setBusy(false);
                    toast.error("Basket not ready", { description: "Pick a curated basket or complete your custom mix (2-20 coins, weights sum to 100%)." });
                    return;
                }
                if (basketSelection.basket_id) payload.basket_id = basketSelection.basket_id;
                if (basketSelection.basket_id_b) payload.basket_id_b = basketSelection.basket_id_b;
                if (basketSelection.basket) payload.basket = basketSelection.basket;
                // notional_mode gets baked into the inline definition too, but
                // for basket_id path we also inject it so the backend can pick
                // it up before writing the swap doc.
                if (payload.basket) payload.basket.notional_mode = basketSelection.notional_mode;
                payload.notional_mode = basketSelection.notional_mode;
                // LIMIT baskets not supported yet (no scalar reference price).
                payload.order_type = "MARKET";
            } else {
                payload.coin = pair.base;
                if (swapType === "multi") payload.cross_coin = pair.quote;
                if (orderType === "LIMIT") {
                    const lp = parseFloat(limitPrice);
                    if (!Number.isFinite(lp) || lp <= 0) {
                        setBusy(false);
                        toast.error("Limit price required", { description: "Enter a target price > 0 for LIMIT orders." });
                        return;
                    }
                    payload.limit_price = lp;
                }
            }
            const { data } = await api.post("/swaps/orders", payload);
            const pairLabel = swapType === "basket"
                ? `${basketSelection?.basket_id_b ? "Basket / Basket" : (payload.basket?.name || basketSelection?.basket_id || "Basket")}`
                : swapType === "multi" ? `${pair.base}-${pair.quote}` : `${pair.base}-USD`;
            const statusBlurb = data?.status === "MATCHED"
                ? " · filled instantly"
                : data?.status === "LIMIT_PENDING"
                    ? ` · LIMIT pending @ ${data.limit_price}`
                    : " · routing to liquidity";
            toast.success(orderType === "LIMIT" ? "Limit order parked" : "Market order placed", {
                description: `${choice === "BULL" ? "Bullish" : "Bearish"} ${pairLabel} · ${parseFloat(notional).toLocaleString()} notional${statusBlurb}`,
            });
            onCreated?.(data);
        } catch (e) {
            toast.error("Order rejected", { description: formatApiError(e.response?.data?.detail) || e.message });
        } finally {
            setBusy(false);
        }
    };

    const choiceLabels = {
        BULL: swapType === "basket"
            ? "Bullish — basket index rises · you win when the weighted level goes up"
            : swapType === "single"
                ? `Bullish — ${pair.base} volatility receivable · ${pair.quote} fixed payable`
                : `Bullish — $${pair.base} volatility receivable · $${pair.quote} volatility payable`,
        BEAR: swapType === "basket"
            ? "Bearish — basket index falls · you win when the weighted level goes down"
            : swapType === "single"
                ? `Bearish — ${pair.base} volatility payable · ${pair.quote} fixed receivable`
                : `Bearish — $${pair.base} volatility payable · $${pair.quote} volatility receivable`,
    };

    return (
        <div
            className="bg-gradient-to-br from-[#0A0A0F] via-[#0A0A0C] to-[#080810] border border-[#1F1F24] rounded-sm overflow-hidden shadow-2xl shadow-cyan-500/5"
            data-testid="swap-ticket"
        >
            {/* Header */}
            <div className="px-4 py-3 border-b border-[#1F1F24] flex items-center justify-between bg-gradient-to-r from-cyan-500/5 to-transparent">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-400 flex items-center gap-1.5">
                        <Sparkles className="w-3 h-3" /> BUBE Ticket
                    </div>
                    <div className="text-sm font-medium font-mono tracking-tight mt-0.5">
                        {swapType === "basket"
                            ? (basketSelection?.mode === "custom"
                                ? (basketSelection?.basket?.name || "Custom basket")
                                : `Basket · ${basketSelection?.basket_id_b ? "vs Basket" : "vs USD"}`)
                            : swapType === "single" ? `${pair.base}-${pair.quote}` : `${pair.base}-${pair.quote} cross`}
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-zinc-500">
                        {swapType === "basket" ? "index level" : `${pair.base} mid`}
                    </div>
                    <div className="font-mono text-sm text-cyan-400 tabular-nums">
                        {swapType === "basket"
                            ? "1.00"
                            : midPrice ? midPrice.toFixed(midPrice >= 1000 ? 2 : 4) : "—"}
                    </div>
                </div>
            </div>

            <div className="p-3 sm:p-4 grid gap-3">
                {/* Type of swap */}
                <FieldLabel label="Type of swap" hint={swapType === "basket" ? "index · basket" : swapType === "multi" ? "cross-asset · BETA" : "directional"}>
                    <SegmentToggle
                        value={swapType}
                        onChange={setTypeAndPair}
                        options={[
                            { value: "single", label: "Single",  sub: "Coin / USD",    testid: "ticket-type-single" },
                            { value: "multi",  label: "Multi",   sub: "Coin / Coin",   testid: "ticket-type-multi" },
                            { value: "basket", label: "Basket",  sub: "Index vs USD",  testid: "ticket-type-basket" },
                        ]}
                    />
                </FieldLabel>

                {/* Order type — MARKET (immediate) vs LIMIT (parked) — baskets are market-only */}
                {swapType !== "basket" && (
                    <FieldLabel
                        label="Order type"
                        hint={orderType === "MARKET" ? "fills immediately" : "fills when price hits target"}
                    >
                        <SegmentToggle
                            value={orderType}
                            onChange={setOrderType}
                            options={[
                                { value: "MARKET", label: "Market", sub: "instant fill", testid: "ticket-order-market" },
                                { value: "LIMIT", label: "Limit", sub: "price target", testid: "ticket-order-limit" },
                            ]}
                        />
                    </FieldLabel>
                )}

                {swapType !== "basket" && orderType === "LIMIT" && (
                    <LimitPriceField
                        value={limitPrice}
                        onChange={setLimitPrice}
                        midPrice={midPrice}
                        swapType={swapType}
                        pair={pair}
                        crossRef={crossRef}
                    />
                )}

                {/* Crypto pair — only for single/multi */}
                {swapType !== "basket" && (
                    <FieldLabel label="Crypto pair" hint={swapType === "single" ? `${pairsData.single.length || 0} live USD pairs` : `${pairsData.multi.length || 0} cross combos`}>
                        {swapType === "single" ? (
                            <PairSelect
                                testid="ticket-pair-single"
                                value={`${pair.base}-USD`}
                                options={pairsData.single.length ? pairsData.single : [{ value: `${pair.base}-USD`, base: pair.base, quote: "USD" }]}
                                onSelect={(o) => setPairAndBubble({ base: o.base, quote: o.quote })}
                            />
                        ) : (
                            <PairSelect
                                testid="ticket-pair-multi"
                                value={`${pair.base}-${pair.quote}`}
                                options={pairsData.multi.length ? pairsData.multi : [{ value: `${pair.base}-${pair.quote}`, base: pair.base, quote: pair.quote }]}
                                onSelect={(o) => setPairAndBubble({ base: o.base, quote: o.quote })}
                                cross
                            />
                        )}
                    </FieldLabel>
                )}

                {/* Basket builder — curated + custom + notional mode + optional second leg */}
                {swapType === "basket" && (
                    <BasketBuilder
                        onChange={setBasketSelection}
                        singlePairs={pairsData.single}
                    />
                )}

                {/* Choice — Bullish / Bearish */}
                <FieldLabel label="Choice">
                    <ChoiceCards
                        value={choice}
                        onChange={setChoice}
                        bullishLabel={choiceLabels.BULL}
                        bearishLabel={choiceLabels.BEAR}
                    />
                </FieldLabel>

                {/* Notional + presets */}
                <FieldLabel label="Notional (USDC)" hint={`exposure size · max ${maxLev}× leverage`}>
                    <div className="grid gap-2">
                        <NumberInput
                            testid="ticket-notional-input"
                            value={notional}
                            onChange={setNotional}
                            prefix="$"
                        />
                        <div className="flex flex-wrap gap-1">
                            {NOTIONAL_PRESETS.map((n) => (
                                <button
                                    key={n}
                                    data-testid={`ticket-notional-${n}`}
                                    onClick={() => {
                                        setNotional(String(n));
                                        const newMin = Math.max(1, n * minPct);
                                        if ((parseFloat(margin) || 0) < newMin) setMargin(String(Math.round(newMin)));
                                    }}
                                    className={`px-2 py-0.5 text-[10px] font-mono rounded-sm border transition-colors ${
                                        parseFloat(notional) === n
                                            ? "border-cyan-400/80 text-cyan-300 bg-cyan-500/10"
                                            : "border-[#1F1F24] text-zinc-500 hover:text-zinc-200 hover:border-zinc-600"
                                    }`}
                                >
                                    {n >= 1000 ? `${n / 1000}k` : n}
                                </button>
                            ))}
                        </div>
                    </div>
                </FieldLabel>

                {/* Margin */}
                <FieldLabel label="Margin (USDC)" hint={`min ${minMargin.toFixed(2)} · max-loss = this amount`}>
                    <NumberInput
                        testid="ticket-margin-input"
                        value={margin}
                        onChange={setMargin}
                        prefix="$"
                    />
                    {/* Margin slider — quick adjust */}
                    <div className="mt-2 flex items-center gap-2">
                        <input
                            type="range"
                            min={minMargin}
                            max={Math.max(minMargin, (parseFloat(notional) || 0) * 0.5)}
                            step={1}
                            value={margin || minMargin}
                            onChange={(e) => setMargin(e.target.value)}
                            className="flex-1 accent-cyan-400 h-1"
                            data-testid="ticket-margin-slider"
                        />
                        <span className="text-[9px] font-mono text-zinc-500 tabular-nums w-12 text-right">
                            {leverage.toFixed(1)}×
                        </span>
                    </div>
                </FieldLabel>

                {/* Duration */}
                <FieldLabel label="Duration · tenor">
                    <div className="flex flex-wrap gap-1">
                        {DURATION_OPTIONS.map((d) => (
                            <button
                                key={d.key}
                                data-testid={`ticket-duration-${d.key}`}
                                onClick={() => setDuration(d.key)}
                                className={`px-2.5 py-1 text-[10px] font-mono rounded-sm border transition-colors ${
                                    duration === d.key
                                        ? "border-cyan-400/80 text-cyan-300 bg-cyan-500/10"
                                        : "border-[#1F1F24] text-zinc-400 hover:text-zinc-100 hover:border-zinc-600"
                                }`}
                            >
                                {d.label}
                            </button>
                        ))}
                    </div>
                </FieldLabel>
            </div>

            {/* Live trade summary */}
            <div className="mx-3 sm:mx-4 mb-3 bg-[#050507] border border-[#1F1F24] rounded-sm p-3">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500">Trade preview</span>
                    <span className="text-[9px] font-mono text-cyan-400">live</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                    <SummaryCell
                        label="Leverage"
                        value={`${leverage.toFixed(1)}×`}
                        accent={leverage > maxLev * 0.7 ? "text-amber-300" : "text-zinc-100"}
                    />
                    <SummaryCell
                        label="Max loss"
                        value={`$${formatNumber(parseFloat(margin) || 0)}`}
                        accent="text-rose-400"
                    />
                    <SummaryCell
                        label={`Entry fee (${entryFeePctLabel(duration)})`}
                        value={`$${fee.toFixed(2)}`}
                    />
                </div>
            </div>

            {/* Info pills */}
            {SHORT_DURATIONS.has(duration) && (
                <InfoPill type="cyan" icon={Zap}>
                    Short tenor <span className="font-semibold">{duration}</span> — min margin <span className="font-semibold">1%</span> (max <span className="font-semibold">{maxLev}×</span>).
                </InfoPill>
            )}
            {LONG_DURATIONS.has(duration) && (
                <InfoPill type="amber" icon={Info}>
                    Long tenor <span className="font-semibold">{duration}</span> — extra <span className="font-semibold">0.10%</span> settlement fee (${((parseFloat(notional) || 0) * 0.001).toFixed(2)}) paid by the loser.
                </InfoPill>
            )}
            {swapType === "multi" && (
                <InfoPill type="violet" icon={ArrowRightLeft}>
                    Multi-swap (BETA) — PnL currently tracks <span className="font-semibold">{pair.base}</span> volatility. Full {pair.base}/{pair.quote} ratio settlement rolling out next.
                </InfoPill>
            )}
            {swapType === "basket" && (
                <InfoPill type="violet" icon={Layers}>
                    Basket — PnL tracks the <span className="font-semibold">weighted index level</span>
                    {basketSelection?.notional_mode === "dynamic"
                        ? " · notional rebases every 24h (gains/losses compound)."
                        : " · notional stays fixed for life of swap."}
                </InfoPill>
            )}
            {warning && (
                <InfoPill type={warning.level === "error" ? "rose" : warning.level === "warning" ? "amber" : "zinc"} icon={AlertTriangle} testid="margin-warning">
                    {warning.msg}
                </InfoPill>
            )}

            {/* Submit */}
            <div className="p-3 border-t border-[#1F1F24]">
                <button
                    data-testid="ticket-submit-button"
                    onClick={submit}
                    disabled={busy || warning?.level === "error"}
                    className={`w-full py-3 text-sm font-semibold tracking-tight rounded-sm transition-all disabled:opacity-40 ${
                        choice === "BULL"
                            ? "bg-emerald-500 hover:bg-emerald-400 text-black shadow-lg shadow-emerald-500/20"
                            : "bg-rose-500 hover:bg-rose-400 text-black shadow-lg shadow-rose-500/20"
                    }`}
                >
                    {busy ? "Placing order…" : swapType === "basket"
                        ? `${orderType === "LIMIT" ? "Park" : "Place"} ${choice === "BULL" ? "Bullish" : "Bearish"} basket ${orderType === "LIMIT" ? "limit" : "market"} →`
                        : `${orderType === "LIMIT" ? "Park" : "Place"} ${choice === "BULL" ? "Bullish" : "Bearish"} ${pair.base}-${pair.quote} ${orderType === "LIMIT" ? "limit" : "market"} →`}
                </button>
                <div className="mt-3 text-[10px] font-mono text-zinc-500 leading-relaxed">
                    Entry fee <span className="text-zinc-300">{entryFeePctLabel(duration)}</span> deducted at match · margin locked until expiry, early exit, or 95% depletion.{" "}
                    <Link to="/fees" data-testid="ticket-fee-link" className="text-cyan-400 hover:text-cyan-300 underline-offset-2 hover:underline">full fee schedule →</Link>
                </div>
            </div>
        </div>
    );
}

/* ---------- Building-block components ---------- */

const FieldLabel = ({ label, hint, children }) => (
    <div className="grid gap-1.5">
        <div className="flex items-baseline justify-between">
            <span className="text-[9.5px] font-mono uppercase tracking-[0.22em] text-zinc-400">{label}</span>
            {hint && <span className="text-[9px] font-mono text-zinc-600 lowercase">{hint}</span>}
        </div>
        {children}
    </div>
);

const SegmentToggle = ({ value, onChange, options }) => (
    <div className={`grid gap-1 p-1 bg-[#050507] border border-[#1F1F24] rounded-sm ${options.length === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
        {options.map((o) => {
            const active = value === o.value;
            return (
                <button
                    key={o.value}
                    data-testid={o.testid}
                    onClick={() => onChange(o.value)}
                    className={`relative py-2 px-3 rounded-sm transition-all text-left ${
                        active
                            ? "bg-gradient-to-br from-cyan-500/15 to-cyan-500/5 border border-cyan-400/60 shadow-[inset_0_1px_0_0_rgba(34,211,238,0.2)]"
                            : "border border-transparent hover:border-zinc-700"
                    }`}
                >
                    <div className={`text-[11px] font-mono uppercase tracking-widest font-semibold ${active ? "text-cyan-300" : "text-zinc-400"}`}>
                        {o.label}
                    </div>
                    <div className="text-[9px] font-mono text-zinc-500 mt-0.5">{o.sub}</div>
                    {active && <Check className="absolute top-1.5 right-1.5 w-3 h-3 text-cyan-400" />}
                </button>
            );
        })}
    </div>
);

function PairSelect({ value, options, onSelect, testid, cross = false }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const ref = useRef(null);
    const inputRef = useRef(null);
    useEffect(() => {
        const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQ(""); } };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);
    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 30);
    }, [open]);
    const current = options.find((o) => o.value === value) || options[0];
    const filtered = useMemo(() => {
        const needle = q.trim().toUpperCase();
        if (!needle) return options;
        return options.filter((o) =>
            o.value.toUpperCase().includes(needle) ||
            (o.base || "").toUpperCase().includes(needle) ||
            (o.quote || "").toUpperCase().includes(needle)
        );
    }, [q, options]);
    const visible = filtered.slice(0, 80);
    return (
        <div className="relative" ref={ref}>
            <button
                data-testid={testid}
                onClick={() => setOpen((s) => !s)}
                className="w-full bg-[#050507] border border-[#1F1F24] hover:border-cyan-500/40 focus:border-cyan-400/80 rounded-sm px-3 py-2.5 flex items-center justify-between text-left transition-colors"
            >
                <span className="flex items-center gap-2 min-w-0">
                    <CoinIcon symbol={current?.base} />
                    {cross && <CoinIcon symbol={current?.quote} small />}
                    <span className="font-mono text-sm text-zinc-100 truncate">{current?.value || "—"}</span>
                </span>
                <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div
                    data-testid={`${testid}-menu`}
                    className="absolute left-0 right-0 top-full mt-1.5 bg-[#0A0A0C] border border-cyan-500/30 rounded-sm shadow-2xl z-30 walkthrough-fade-in"
                >
                    <div className="p-2 border-b border-[#1F1F24] flex items-center gap-2 bg-[#070709]">
                        <Search className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                        <input
                            ref={inputRef}
                            data-testid={`${testid}-search`}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder={`Search ${options.length} pairs…`}
                            className="flex-1 bg-transparent border-none outline-none text-[12px] font-mono text-zinc-100 placeholder:text-zinc-600"
                        />
                        <span className="text-[9px] font-mono text-zinc-500 shrink-0">{filtered.length}</span>
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                        {visible.length === 0 && (
                            <div className="px-3 py-6 text-center text-[11px] font-mono text-zinc-600">no matches</div>
                        )}
                        {visible.map((o) => {
                            const active = o.value === current?.value;
                            return (
                                <button
                                    key={o.value}
                                    data-testid={`${testid}-opt-${o.value}`}
                                    onClick={() => { onSelect(o); setOpen(false); setQ(""); }}
                                    className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
                                        active ? "bg-cyan-500/15 text-cyan-300" : "text-zinc-300 hover:bg-zinc-800/60 hover:text-zinc-100"
                                    }`}
                                >
                                    <CoinIcon symbol={o.base} />
                                    {cross && <CoinIcon symbol={o.quote} small />}
                                    <span className="font-mono text-sm flex-1 truncate">{o.value}</span>
                                    {typeof o.mid === "number" && (
                                        <span className="font-mono text-[10px] text-zinc-500 tabular-nums">
                                            {o.mid >= 1000 ? o.mid.toFixed(2) : o.mid >= 1 ? o.mid.toFixed(4) : o.mid.toFixed(6)}
                                        </span>
                                    )}
                                    {active && <Check className="w-3.5 h-3.5 text-cyan-400" />}
                                </button>
                            );
                        })}
                        {filtered.length > visible.length && (
                            <div className="px-3 py-2 text-center text-[10px] font-mono text-zinc-600 border-t border-[#1F1F24]">
                                showing first {visible.length} of {filtered.length} — refine search
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

const COIN_THEME = {
    BTC:  { bg: "bg-orange-500", t: "₿", text: "text-white" },
    ETH:  { bg: "bg-zinc-200", t: "Ξ", text: "text-zinc-900" },
    SOL:  { bg: "bg-violet-500", t: "S", text: "text-white" },
    HYPE: { bg: "bg-cyan-400", t: "H", text: "text-black" },
    XRP:  { bg: "bg-zinc-800", t: "✕", text: "text-white" },
    DOGE: { bg: "bg-amber-400", t: "Ð", text: "text-black" },
    AVAX: { bg: "bg-rose-500", t: "A", text: "text-white" },
    USD:  { bg: "bg-emerald-500", t: "$", text: "text-black" },
};
// Deterministic colour palette for any other coin — picks a slot by char codes
const _FALLBACK_PALETTE = [
    { bg: "bg-blue-500", text: "text-white" },
    { bg: "bg-fuchsia-500", text: "text-white" },
    { bg: "bg-teal-500", text: "text-white" },
    { bg: "bg-indigo-500", text: "text-white" },
    { bg: "bg-pink-500", text: "text-white" },
    { bg: "bg-lime-500", text: "text-black" },
    { bg: "bg-sky-500", text: "text-white" },
    { bg: "bg-yellow-500", text: "text-black" },
];
const _themeFor = (symbol) => {
    if (!symbol) return { bg: "bg-zinc-500", t: "•", text: "text-white" };
    if (COIN_THEME[symbol]) return COIN_THEME[symbol];
    const code = symbol.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
    const slot = _FALLBACK_PALETTE[code % _FALLBACK_PALETTE.length];
    return { ...slot, t: symbol.charAt(0) };
};
const CoinIcon = ({ symbol, small = false }) => {
    const v = _themeFor(symbol);
    return (
        <span className={`shrink-0 rounded-full grid place-items-center font-bold ${v.bg} ${v.text} ${small ? "w-4 h-4 text-[9px]" : "w-5 h-5 text-[10px]"}`}>
            {v.t}
        </span>
    );
};

function ChoiceCards({ value, onChange, bullishLabel, bearishLabel }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
                data-testid="ticket-choice-bull"
                onClick={() => onChange("BULL")}
                className={`relative text-left rounded-sm border-2 px-3 py-2.5 transition-all ${
                    value === "BULL"
                        ? "border-emerald-400 bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 shadow-[0_0_0_3px_rgba(16,185,129,0.12)]"
                        : "border-[#1F1F24] hover:border-emerald-500/40 bg-[#050507]"
                }`}
            >
                <div className="flex items-center gap-1.5">
                    <TrendingUp className={`w-3.5 h-3.5 ${value === "BULL" ? "text-emerald-300" : "text-emerald-400/70"}`} />
                    <span className={`text-[11px] font-mono uppercase tracking-widest font-semibold ${value === "BULL" ? "text-emerald-300" : "text-zinc-400"}`}>
                        Bullish
                    </span>
                    {value === "BULL" && <Check className="ml-auto w-3.5 h-3.5 text-emerald-400" />}
                </div>
                <div className="text-[10px] font-mono text-zinc-400 mt-1.5 leading-snug">{bullishLabel}</div>
            </button>
            <button
                data-testid="ticket-choice-bear"
                onClick={() => onChange("BEAR")}
                className={`relative text-left rounded-sm border-2 px-3 py-2.5 transition-all ${
                    value === "BEAR"
                        ? "border-rose-400 bg-gradient-to-br from-rose-500/20 to-rose-500/5 shadow-[0_0_0_3px_rgba(244,63,94,0.12)]"
                        : "border-[#1F1F24] hover:border-rose-500/40 bg-[#050507]"
                }`}
            >
                <div className="flex items-center gap-1.5">
                    <TrendingDown className={`w-3.5 h-3.5 ${value === "BEAR" ? "text-rose-300" : "text-rose-400/70"}`} />
                    <span className={`text-[11px] font-mono uppercase tracking-widest font-semibold ${value === "BEAR" ? "text-rose-300" : "text-zinc-400"}`}>
                        Bearish
                    </span>
                    {value === "BEAR" && <Check className="ml-auto w-3.5 h-3.5 text-rose-400" />}
                </div>
                <div className="text-[10px] font-mono text-zinc-400 mt-1.5 leading-snug">{bearishLabel}</div>
            </button>
        </div>
    );
}

const NumberInput = ({ value, onChange, prefix, testid }) => (
    <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm text-zinc-500 pointer-events-none">{prefix}</span>}
        <input
            data-testid={testid}
            type="number"
            min={0}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`w-full bg-[#050507] border border-[#1F1F24] focus:border-cyan-400/80 rounded-sm py-2.5 ${prefix ? "pl-7" : "pl-3"} pr-3 font-mono text-sm text-zinc-100 outline-none transition-colors tabular-nums`}
        />
    </div>
);

function LimitPriceField({ value, onChange, midPrice, swapType, pair, crossRef }) {
    const ref = swapType === "multi" ? crossRef : midPrice;
    const refLabel = swapType === "multi" ? `${pair.base}/${pair.quote} ratio` : `${pair.base} mid`;
    const lp = parseFloat(value);
    const direction = ref && Number.isFinite(lp) && lp > 0
        ? (lp >= ref ? "above" : "below")
        : null;
    const fmt = (n) => {
        if (!Number.isFinite(n)) return "—";
        if (n >= 1000) return n.toFixed(2);
        if (n >= 1) return n.toFixed(4);
        return n.toFixed(6);
    };
    const setPct = (pct) => {
        if (!ref) return;
        onChange(String((ref * (1 + pct / 100)).toFixed(ref >= 1000 ? 2 : 6)));
    };
    return (
        <FieldLabel
            label="Limit price"
            hint={ref ? `${refLabel} · ${fmt(ref)}` : "live ref unavailable"}
        >
            <div className="grid gap-2">
                <NumberInput
                    testid="ticket-limit-price"
                    value={value}
                    onChange={onChange}
                    prefix={swapType === "multi" ? "" : "$"}
                />
                <div className="flex flex-wrap gap-1">
                    {[-5, -1, 0, +1, +5].map((p) => (
                        <button
                            key={p}
                            data-testid={`ticket-limit-pct-${p}`}
                            onClick={() => setPct(p)}
                            disabled={!ref}
                            className="px-2 py-0.5 text-[10px] font-mono rounded-sm border border-[#1F1F24] text-zinc-500 hover:text-zinc-200 hover:border-zinc-600 disabled:opacity-40"
                        >
                            {p === 0 ? "at mid" : `${p > 0 ? "+" : ""}${p}%`}
                        </button>
                    ))}
                </div>
                {direction && (
                    <div className={`text-[9.5px] font-mono px-2 py-1 rounded-sm border ${
                        direction === "above"
                            ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-300"
                            : "border-rose-500/30 bg-rose-500/5 text-rose-300"
                    }`}>
                        Fires when {refLabel} {direction === "above" ? "rises to ≥" : "falls to ≤"} {fmt(lp)} (breakout / stop · GTE/LTE auto-detected).
                    </div>
                )}
            </div>
        </FieldLabel>
    );
}

const SummaryCell = ({ label, value, accent = "text-zinc-100" }) => (
    <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm px-2.5 py-2">
        <div className="text-[8.5px] font-mono uppercase tracking-[0.18em] text-zinc-500">{label}</div>
        <div className={`font-mono text-sm mt-1 tabular-nums ${accent}`}>{value}</div>
    </div>
);

const InfoPill = ({ type, icon: Icon, children, testid }) => {
    const theme = {
        cyan:   "border-cyan-500/30 bg-cyan-500/5 text-cyan-300",
        amber:  "border-amber-500/30 bg-amber-500/5 text-amber-300",
        rose:   "border-rose-500/40 bg-rose-500/10 text-rose-200",
        violet: "border-violet-500/30 bg-violet-500/5 text-violet-300",
        zinc:   "border-zinc-700 bg-zinc-900/50 text-zinc-300",
    }[type] || "border-zinc-700 bg-zinc-900/50 text-zinc-300";
    return (
        <div
            data-testid={testid}
            className={`mx-3 sm:mx-4 mb-2 flex items-start gap-2 px-3 py-2 rounded-sm border ${theme}`}
        >
            <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div className="text-[10px] font-mono leading-relaxed">{children}</div>
        </div>
    );
};
